import {
  parseQuickSwitcherScopedQuery,
  type QuickSearchScope,
} from "@/lib/quickSwitcherScopedQuery";

export interface BatchDetailsUrlSearchParams {
  /** `?search=` — batch list filter (supports `batch:`, `@batch`, `so:`, …). */
  search?: string;
  /** `?search_batch=` — sugar for batch-scoped list filters (maps to `batch:<id>`). */
  searchBatch?: string;
}

export type BatchDetailsSearchRow = {
  batch_id: number;
  ship_to_recipient: string | null;
  manufacturer_sales_order_number: string | null;
  gso_numbers: string[];
  end_user_names: string[];
  location_label: string;
  mfr_ship_by_date: string | null;
};

function joinSearchParts(parts: Array<string | number | null | undefined>): string {
  return parts
    .filter((value) => value != null && String(value).trim() !== "")
    .join(" ")
    .toLowerCase();
}

function effectiveBatchDetailsSearchScope(scope: QuickSearchScope): QuickSearchScope {
  if (scope === "page" || scope === "wiki" || scope === "field" || scope === "sku" || scope === "shipment") {
    return "all";
  }
  return scope;
}

/** Canonical in-field batch filter for deep links (`batch:2050`). */
export function batchDetailsBatchSearchDeepLink(batchId: number | string): string {
  return `batch:${String(batchId).trim()}`;
}

/**
 * Resolve batch-details list search text from URL query params.
 * Prefer `search=`; `search_batch=` expands to `batch:<id>`.
 * `batch_id=` is selection only and must not become a list filter.
 */
export function batchDetailsSearchFromUrlParams(params: BatchDetailsUrlSearchParams): string {
  const search = params.search?.trim() ?? "";
  if (search !== "") {
    return search;
  }
  const searchBatch = params.searchBatch?.trim() ?? "";
  if (searchBatch !== "") {
    return batchDetailsBatchSearchDeepLink(searchBatch);
  }
  return "";
}

export function batchDetailsSearchHaystackForScope(
  row: BatchDetailsSearchRow,
  scope: QuickSearchScope,
): string {
  switch (scope) {
    case "batch":
      return String(row.batch_id);
    case "so":
      return joinSearchParts(row.gso_numbers ?? []);
    case "end-user":
      return joinSearchParts(row.end_user_names ?? []);
    case "po":
      return "";
    case "all":
    default:
      return joinSearchParts([
        row.batch_id,
        row.ship_to_recipient,
        row.manufacturer_sales_order_number,
        ...(row.gso_numbers ?? []),
        ...(row.end_user_names ?? []),
        row.location_label,
        row.mfr_ship_by_date,
      ]);
  }
}

export function parseBatchDetailsSearchQuery(raw: string): { scope: QuickSearchScope; term: string } {
  return parseQuickSwitcherScopedQuery(raw);
}

/** Plain term sent to the server quick-search endpoint after removing a supported scope prefix. */
export function batchDetailsServerSearchTerm(rawQuery: string): string {
  return parseBatchDetailsSearchQuery(rawQuery).term.trim();
}

export function matchesBatchDetailsSearch(row: BatchDetailsSearchRow, rawQuery: string): boolean {
  const parsed = parseBatchDetailsSearchQuery(rawQuery);
  const term = parsed.term.trim().toLowerCase();
  const scope = effectiveBatchDetailsSearchScope(parsed.scope);

  if (term === "") {
    return scope === "all";
  }

  if (scope === "batch") {
    return String(row.batch_id).toLowerCase() === term;
  }

  const haystack = batchDetailsSearchHaystackForScope(row, scope);
  if (haystack === "") {
    return false;
  }
  return haystack.includes(term);
}

/** True when the list filter is only an exact batch-id scope (`batch:2050` / `@batch 2050`). */
export function batchDetailsSearchIsExactBatchIdFilter(rawQuery: string, batchId: number): boolean {
  const parsed = parseBatchDetailsSearchQuery(rawQuery);
  if (parsed.scope !== "batch") return false;
  return parsed.term.trim() === String(batchId);
}

export function filterBatchDetailsRows<T extends BatchDetailsSearchRow>(
  rows: readonly T[],
  rawQuery: string,
): T[] {
  const trimmed = rawQuery.trim();
  if (trimmed === "") {
    return [...rows];
  }
  return rows.filter((row) => matchesBatchDetailsSearch(row, trimmed));
}
